import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { resolvableServiceValueForm2ApiMapper } from '../../ResolvableService/mappers/resolvable-service-value.form2api.mapper';
import type { SubmitHandler } from '../../ResolvableServiceUser/types/submit-handler.type';

import type { ManualTotalFormValues } from '../types/values.type';
import type { UpdateManualTotalMutation } from '../types/props.type';

interface Params {
  readonly mutation: UpdateManualTotalMutation;
  readonly onSuccess: () => void;
  readonly queryKeysToInvalidate: readonly (readonly string[])[];
  readonly serviceUsagesGroupId: Id;
  readonly serviceUserId: Id;
}

export const useUpdateManualTotalSubmit = ({
  mutation,
  onSuccess,
  queryKeysToInvalidate,
  serviceUsagesGroupId,
  serviceUserId,
}: Readonly<Params>) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit: SubmitHandler<ManualTotalFormValues> = useCallback(
    ({ values, memos, formikHelpers: { setSubmitting }, setFormError }) => {
      setFormError(null);

      mutation(
        {
          manualTotal: resolvableServiceValueForm2ApiMapper(values.value),
          serviceUsagesGroupId,
          serviceUserId,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: (error: unknown) => {
            setFormError(userFriendlyErrors(error as BackError));
            setSubmitting(false);
          },
          onSuccess: () => {
            queryKeysToInvalidate.forEach((queryKey) => {
              void queryClient.invalidateQueries({ queryKey });
            });
            enqueueSnackbar(
              'Les valeurs du total manuel ont bien été mises à jour.',
            );
            setSubmitting(false);
            onSuccess();
          },
        },
      );

      return undefined;
    },
    [
      enqueueSnackbar,
      mutation,
      onSuccess,
      queryClient,
      queryKeysToInvalidate,
      serviceUsagesGroupId,
      serviceUserId,
    ],
  );

  return { handleSubmit };
};
